import React from 'react';
import { Box, IconButton, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import { Language as LanguageIcon } from '@mui/icons-material';
import { useT } from '@hooks/useT';

const languages = [
  { code: 'fr', label: 'Français', flag: '⚜️' },
  { code: 'en', label: 'English', flag: '🇨🇦' },
] as const;

type LanguageCode = (typeof languages)[number]['code'];

interface LanguageSelectorProps {
  /** Variant: 'floating' for fixed position, 'inline' for normal flow */
  variant?: 'floating' | 'inline';
  /** If true, reload the page after changing language (useful for PayPal integration) */
  reloadOnChange?: boolean;
  /** If true, display the current language code beside the button */
  showLabel?: boolean;
}

/**
 * Language selector component.
 * - 'floating': Fixed position bottom-right corner (for App level)
 * - 'inline': Normal flow (for embedding in menus/toolbars)
 */
function LanguageSelector({ variant = 'inline', reloadOnChange = false, showLabel = false }: Readonly<LanguageSelectorProps>) {
  const { currentLang, changeLanguage } = useT();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const currentLanguage = languages.find((l) => l.code === currentLang) ?? languages[0];

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (code: LanguageCode) => {
    if (reloadOnChange) {
      // Change language and reload to avoid PayPal SDK issues
      void changeLanguage(code).then(() => {
        window.location.reload();
      });
    } else {
      void changeLanguage(code);
    }
    handleClose();
  };

  const floatingStyles = variant === 'floating' ? {
    position: 'fixed' as const,
    top: 16,
    right: 16,
    zIndex: 1300,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    boxShadow: 2,
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 1)',
    },
  } : {};

  return (
    <>
      <Tooltip title={currentLanguage.label}>
        <Box
          onClick={handleClick}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            cursor: 'pointer',
            ...floatingStyles,
          }}
          data-testid="language-selector-button"
        >
          <IconButton
            size="small"
            sx={{
              color: 'primary.main',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <LanguageIcon />
          </IconButton>
          {showLabel && (
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontWeight: 500,
                textTransform: 'uppercase',
                fontSize: '0.875rem',
              }}
            >
              {currentLanguage.code}
            </Typography>
          )}
        </Box>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        disableScrollLock
        data-testid="language-selector-menu"
      >
        {languages.map((lang) => (
          <MenuItem
            key={lang.code}
            onClick={() => handleSelect(lang.code)}
            selected={lang.code === currentLang}
          >
            {lang.flag} {lang.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

export default LanguageSelector;
